'use client';
// Mining — live SHA3-256 proof-of-work with a real-time hash rate.
// Rewards go EXCLUSIVELY to the active session wallet — each user mines for their own address only.
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Pickaxe, Play, Square, Zap, Gauge, Timer, Cpu, Hash, Target, Boxes,
} from 'lucide-react';
import {
  fetchJson, usePoll, post, fmtAmt, fmtInt, fmtHashrate, fmtDuration, timeAgo,
  SectionHeader, AddressChip, Spinner, Mono, EmptyState,
} from './shared';
import { useSession, ConnectPrompt } from './session';
import type { Net, MiningStatus, NetStats, BlockT } from './types';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';

export function Mining({ net }: { net: Net }) {
  const { session, wallet } = useSession();
  const { data, refresh } = usePoll<{ mining: MiningStatus; autoMine: boolean }>(
    async () => fetchJson(`/api/mine?net=${net}`), 1500, [net]);
  const { data: stats } = usePoll<{ network: NetStats }>(
    async () => fetchJson(`/api/stats?net=${net}`), 2500, [net]);
  const { data: blocksData } = usePoll<{ blocks: BlockT[] }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=blocks&limit=8`), 3000, [net]);

  const m = data?.mining;
  const myAddress = session?.address;
  const iAmMining = Boolean(m?.running && m.miner === myAddress);
  const someoneElseMining = Boolean(m?.running && m.miner !== myAddress);

  // Approximate progress: 1 - e^(-attempts/expected)
  const expected = m?.expectedAttempts ?? Math.pow(16, 5);
  const progress = m?.running ? Math.min(99.9, (1 - Math.exp(-m.attempts / expected)) * 100) : 0;

  const start = async () => {
    if (!myAddress) return;
    try {
      await post(`/api/mine?net=${net}`, { action: 'start', miner: myAddress });
      toast.success('Mining started — hunting for the next block for your wallet');
      refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to start'); }
  };

  const stop = async () => {
    try { await post(`/api/mine?net=${net}`, { action: 'stop' }); toast.info('Mining stopped'); refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to stop'); }
  };

  const toggleAuto = async (v: boolean) => {
    try {
      await post(`/api/mine?net=${net}`, { action: 'auto', auto: v });
      toast.success(v ? 'The automatic node is on — any pending transaction is mined instantly' : 'The automatic node is off');
      refresh();
    }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Error'); }
  };

  if (!session) {
    return (
      <div className="space-y-5">
        <SectionHeader icon={Pickaxe} title="AmanChain miner" desc="Real proof-of-work: find a nonce making the SHA3-256 hash of the block header start with difficulty zeros" />
        <ConnectPrompt what="start mining for your own wallet" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionHeader icon={Pickaxe} title="AmanChain miner" desc="Real proof-of-work: find a nonce making the SHA3-256 hash of the block header start with difficulty zeros" />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Control unit */}
        <div className="lg:col-span-2 glass p-6 relative overflow-hidden">
          {m?.running && <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />}
          <div className="relative space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`rounded-xl p-3 ring-1 ${m?.running ? 'bg-amber-500/10 ring-amber-500/30 text-amber-400 animate-pulse' : 'bg-muted ring-border text-muted-foreground'}`}>
                  <Pickaxe className="size-6" />
                </div>
                <div>
                  <div className="font-bold">{iAmMining ? 'Your miner is running' : someoneElseMining ? 'Another wallet is mining' : 'Miner stopped'}</div>
                  <div className="text-xs text-muted-foreground">
                    {iAmMining ? `Searching for block #${fmtInt((stats?.network.height ?? 0) + 1)} — rewards land on your address`
                      : someoneElseMining ? 'Stop it from their session, or wait — one miner at a time on this node'
                      : 'Rewards go exclusively to your wallet below'}
                  </div>
                </div>
              </div>
              {iAmMining
                ? <Button variant="destructive" onClick={stop} className="gap-2 font-bold"><Square className="size-4" /> Stop</Button>
                : <Button onClick={start} disabled={m?.running === true} className="gap-2 font-bold"><Play className="size-4" /> Start mining</Button>}
            </div>

            <div className="rounded-lg bg-muted/40 ring-1 ring-border p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[0.65rem] text-muted-foreground mb-1">Reward wallet — your session only</div>
                <div className="flex items-center gap-2"><AddressChip address={myAddress ?? ''} /></div>
                <div className="text-[0.65rem] text-muted-foreground mt-1">{fmtAmt(wallet?.balance ?? 0)} AMAN · {fmtAmt(wallet?.staked ?? 0)} staked</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[0.65rem] text-muted-foreground">Current reward</div>
                <div className="text-lg font-black text-amber-400" dir="ltr">{fmtAmt(stats?.network.blockReward)} AMAN</div>
                <div className="text-[0.6rem] text-muted-foreground">per block</div>
              </div>
            </div>

            {m?.running && (
              <>
                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-muted-foreground">Probability of having found the block so far</span>
                    <span className="mono" dir="ltr">{m.attempts.toLocaleString('en-US')} / ~{expected.toLocaleString('en-US')}</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <div className="text-[0.62rem] text-muted-foreground mt-1">Proof-of-work is a random process — the block may arrive on the first attempt or after a million</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <MiniStat icon={Cpu} label="Hash rate" value={fmtHashrate(m.hashRate)} accent="text-amber-400" />
                  <MiniStat icon={Hash} label="Attempts" value={m.attempts.toLocaleString('en-US')} />
                  <MiniStat icon={Timer} label="Elapsed" value={fmtDuration(m.elapsed)} />
                  <MiniStat icon={Target} label="Difficulty" value={String(m.difficulty)} accent="text-emerald-400" />
                </div>
              </>
            )}

            <div className="flex items-center justify-between rounded-lg bg-muted/40 ring-1 ring-border p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-sky-500/10 text-sky-400 ring-1 ring-sky-500/25 p-2"><Zap className="size-4" /></div>
                <div>
                  <div className="text-sm font-bold">Automatic node</div>
                  <div className="text-[0.68rem] text-muted-foreground">Mines any transaction entering the mempool instantly — ideal for confirming transfers</div>
                </div>
              </div>
              <Switch checked={Boolean(data?.autoMine)} onCheckedChange={toggleAuto} aria-label="Automatic node" />
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-3">
          <div className="glass p-5">
            <div className="text-sm font-bold mb-3 flex items-center gap-2"><Gauge className="size-4 text-emerald-400" /> Mining economics</div>
            <div className="space-y-2.5 text-xs">
              <Row k="Current block reward" v={`${fmtAmt(stats?.network.blockReward)} AMAN`} />
              <Row k="Miner's fee share" v="50% of every transaction fee" />
              <Row k="Halving" v="every 50,000 blocks" />
              <Row k="Target block time" v={`${stats?.network.targetBlockTime ?? 12} seconds`} />
              <Row k="Next difficulty" v={String(stats?.network.difficulty ?? '—')} />
              <Row k="Last block took" v={stats?.network.lastBlock ? fmtDuration(stats.network.lastBlock.miningTime) : '—'} />
            </div>
            <p className="text-[0.65rem] text-muted-foreground mt-3 leading-relaxed">
              Difficulty retargets automatically every 5 blocks to keep block time near the target — the same
              principle that stabilizes any proof-of-work network, tuned here for fast finality.
            </p>
          </div>
          {m?.lastBlockHash && (
            <div className="glass p-5">
              <div className="text-sm font-bold mb-2 text-emerald-400 flex items-center gap-2"><Boxes className="size-4" /> Last block you mined</div>
              <Mono className="text-[0.7rem] break-all leading-relaxed">{m.lastBlockHash}</Mono>
              <div className="text-xs text-muted-foreground mt-1">Block #{fmtInt(m.lastBlockHeight)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Recent blocks */}
      <div className="glass p-5">
        <div className="text-sm font-bold mb-3">Recent mined blocks</div>
        {!blocksData && <Spinner />}
        {blocksData && blocksData.blocks.length <= 1 && <EmptyState icon={Pickaxe} title="No mined blocks yet" desc="Start mining or enable the automatic node" />}
        <div className="grid md:grid-cols-2 gap-2">
          {(blocksData?.blocks ?? []).filter(b => b.height > 0).map(b => (
            <div key={b.hash} className="rounded-lg border border-border/60 bg-muted/30 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-emerald-400" dir="ltr">#{fmtInt(b.height)}</span>
                  <Mono>{b.hash.slice(0, 14)}…</Mono>
                </div>
                <div className="text-[0.62rem] text-muted-foreground mt-1 flex items-center gap-2">
                  <span>{b.transactions.length} txs</span>•<span>{fmtDuration(b.miningTime)}</span>•<span>{timeAgo(b.timestamp)}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-amber-400" dir="ltr">+{fmtAmt(b.reward)}</div>
                <div className="text-[0.6rem] text-muted-foreground flex items-center gap-1"><AddressChip address={b.miner} /></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, accent }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value: string; accent?: string;
}) {
  return (
    <div className="rounded-lg bg-muted/40 ring-1 ring-border p-3">
      <div className="flex items-center gap-1.5 text-[0.62rem] text-muted-foreground"><Icon className="size-3" /> {label}</div>
      <div className={`mono text-sm font-bold mt-1 ${accent ?? ''}`} dir="ltr">{value}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-bold text-right" dir="ltr">{v}</span>
    </div>
  );
}
